import { DatePipe } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

/** The store description's limit; the API enforces the same. */
export const DESCRIPTION_MAX = 150;

interface MerchantProfile {
  id: number;
  brandName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
  logoUrl: string | null;
  description: string;
  isStarSeller: boolean;
  memberSince: string | null;
}

/**
 * The merchant's own profile: brand name, logo, contact and address as
 * FishyHub has them, and the store description - the few lines shown under
 * the logo on the store's page, above Follow and Share.
 *
 * Only the description is edited here. The logo changes from the photo in
 * the menu; brand name, email, contact and address are kept by FishyHub (they
 * are how the merchant logs in and is paid), so a change goes through support.
 * The API reads and writes only the logged-in merchant's own row.
 */
@Component({
  selector: "fh-profile",
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, DatePipe],
  templateUrl: "./profile.component.html",
  styles: [
    `
      .profile-head {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 20px;
      }
      .logo {
        width: 72px;
        height: 72px;
        border-radius: 50%;
        object-fit: cover;
        border: 1px solid #e5e7eb;
        background: #f3f4f6;
        flex-shrink: 0;
      }
      .logo-initial {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        font-weight: 600;
        color: #006ddf;
      }
      .brand {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
      }
      .details {
        display: grid;
        grid-template-columns: 160px 1fr;
        gap: 8px 16px;
        margin: 0 0 24px;
      }
      .details dt {
        color: #6b7280;
        font-weight: 500;
      }
      .details dd {
        margin: 0;
        word-break: break-word;
      }
      textarea {
        width: 100%;
        min-height: 90px;
        padding: 10px 12px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font: inherit;
        resize: vertical;
      }
      .counter {
        font-size: 12px;
        color: #6b7280;
        text-align: right;
      }
      .counter.over {
        color: #c62828;
        font-weight: 600;
      }
      @media (max-width: 600px) {
        .details {
          grid-template-columns: 1fr;
          gap: 2px;
        }
        .details dd {
          margin-bottom: 8px;
        }
      }
    `,
  ],
})
export class ProfileComponent implements OnInit {
  readonly max = DESCRIPTION_MAX;
  readonly descriptionControl = new FormControl<string>("", { nonNullable: true });

  profile = signal<MerchantProfile | null>(null);
  loading = signal(true);
  saving = signal(false);
  success = signal("");
  error = signal("");
  length = signal(0);
  tooLong = computed(() => this.length() > DESCRIPTION_MAX);

  constructor(private requestService: RequestService) {
    // Counted as it is saved: the server folds runs of whitespace to one space
    // and trims, so a description that fits there is never refused here.
    this.descriptionControl.valueChanges.subscribe((v) =>
      this.length.set([...(v ?? "").replace(/\s+/g, " ").trim()].length)
    );
  }

  ngOnInit(): void {
    this.requestService.get<IApiResponse>("/api/v2/merchant-profile").subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.status && res.data) {
          const p = res.data as MerchantProfile;
          this.profile.set(p);
          this.descriptionControl.setValue(p.description ?? "");
        } else {
          this.error.set(res.message || "Could not load your profile");
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set("Could not load your profile");
      },
    });
  }

  /** "+65 9123 4567", or "—" */
  phone(p: MerchantProfile): string {
    const n = p.phoneNumber?.trim();
    if (!n) return "—";
    const code = p.phoneCountryCode?.trim();
    return code ? `${code.startsWith("+") ? code : `+${code}`} ${n}` : n;
  }

  /** The address lines, postal code, city and state on one line, or "—". */
  address(p: MerchantProfile): string {
    const parts = [
      p.addressLine1,
      p.addressLine2,
      [p.city, p.state].filter(Boolean).join(", "),
      p.postalCode,
    ]
      .map((s) => (s ?? "").trim())
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "—";
  }

  save(): void {
    if (this.saving() || this.tooLong()) return;
    this.saving.set(true);
    this.success.set("");
    this.error.set("");
    this.requestService
      .put<IApiResponse>("/api/v2/merchant-profile", { description: this.descriptionControl.value })
      .subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res.status) {
            const saved = (res.data as { description: string })?.description ?? "";
            this.descriptionControl.setValue(saved);
            this.success.set(
              saved
                ? "Saved - it now shows under your logo on your store page"
                : "Description removed"
            );
          } else {
            this.error.set(res.message || "Could not save your description");
          }
        },
        error: () => {
          this.saving.set(false);
          this.error.set("Could not save your description");
        },
      });
  }
}
